import React, { useCallback, useEffect, useState } from "react";
import Header from "../../directives/header";
import Footer from "../../directives/footer";
import Sidebar from "../../directives/sidebar";
import toast, { Toaster } from "react-hot-toast";
import moment from "moment";
import {
  closeSeedRoundAction,
  exportSeedRoundAction,
  getSeedRoundStatusAction,
  openSeedRoundAction,
  reconcileSeedRoundAction,
} from "../../Action/action";
import config from "../../coreFIles/config";
import { showConfirm } from "../../utilities/alert";

const ROUND_ID = "current";

const statCards = (round, isClosed) => [
  {
    label: "Round ID",
    value: round?.roundId || ROUND_ID,
  },
  {
    label: "Status",
    value: round?.status || "OPEN",
    badge: true,
    isClosed,
  },
  {
    label: "Closed At",
    value: round?.closedAt
      ? moment(round.closedAt).format("DD/MM/YY HH:mm")
      : "—",
    small: true,
  },
  {
    label: "Last Export Rows",
    value: round?.lastExportRowCount ?? "—",
  },
];

const SeedRound = () => {
  const [loader, setLoader] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [round, setRound] = useState(null);
  const [exportInfo, setExportInfo] = useState(null);

  const fetchStatus = useCallback(async () => {
    if (!config.seedRoundApiUrl) {
      return;
    }

    setLoader(true);
    try {
      const res = await getSeedRoundStatusAction({ roundId: ROUND_ID });
      if (res?.status) {
        setRound(res.data);
      } else {
        toast.error(res?.msg || "Failed to fetch seed round status");
      }
    } catch (error) {
      const message =
        error?.response?.data?.msg ||
        error?.message ||
        "Failed to fetch seed round status";
      toast.error(message);
    } finally {
      setLoader(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const runAction = async (actionKey, actionFn, title, confirmText) => {
    if (!config.seedRoundApiUrl) {
      toast.error("Seed round API URL is not configured in config.js");
      return;
    }

    const confirmed = await showConfirm(title, confirmText);
    if (!confirmed) {
      return;
    }

    setActionLoading(actionKey);
    try {
      const res = await actionFn({ roundId: ROUND_ID });
      if (res?.status) {
        toast.success(res.msg || "Action completed");
        if (res.data?.round) {
          setRound(res.data.round);
        } else if (res.data) {
          setRound(res.data);
        }
        if (res.data?.bucket && res.data?.key) {
          setExportInfo({
            bucket: res.data.bucket,
            key: res.data.key,
            rowCount: res.data.rowCount,
          });
        }
        await fetchStatus();
      } else {
        toast.error(res?.msg || "Action failed");
      }
    } catch (error) {
      const message =
        error?.response?.data?.msg || error?.message || "Action failed";
      toast.error(message);
    } finally {
      setActionLoading("");
    }
  };

  const isClosed = round?.status === "CLOSED";
  const hasExport = Boolean(round?.lastExportKey);

  return (
    <>
      <div className="wrapper h-auto seed-round-page" style={{ height: "100vh" }}>
        <Header />
        <Sidebar />
        <Toaster />

        <div
          className="content-wrapper"
          style={{ height: "100vh", overflowY: "scroll" }}
        >
          <div className="container-full">
            <section className="content">
              <div className="row">
                <div className="col-lg-12 col-12">
                  <div className="box">
                    <div className="box-header with-border d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <h3 className="box-title" style={{ fontWeight: 600 }}>
                        Seed Round Management
                      </h3>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={fetchStatus}
                        disabled={loader || Boolean(actionLoading)}
                      >
                        <i className="fa fa-refresh" /> Refresh
                      </button>
                    </div>

                    <div className="box-body">
                      {!config.seedRoundApiUrl && (
                        <div className="alert alert-warning">
                          <strong>Config required:</strong> Set{" "}
                          <code>seedRoundApiUrl</code> in{" "}
                          <code>src/coreFIles/config.js</code>.
                        </div>
                      )}

                      {loader && !round ? (
                        <center style={{ padding: "40px 0" }}>
                          <h4>
                            <i className="fa fa-spinner fa-spin" /> Please wait
                          </h4>
                        </center>
                      ) : (
                        <>
                          <div className="row">
                            {statCards(round, isClosed).map((card) => (
                              <div
                                key={card.label}
                                className="col-lg-3 col-md-6 col-12 mb-3"
                              >
                                <div className="seed-round-card h-100">
                                  <span className="seed-round-label">
                                    {card.label}
                                  </span>
                                  {card.badge ? (
                                    <span
                                      className={`badge seed-round-badge ${
                                        card.isClosed
                                          ? "bg-danger"
                                          : "bg-success"
                                      }`}
                                    >
                                      {card.value}
                                    </span>
                                  ) : (
                                    <p
                                      className={`seed-round-value ${
                                        card.small ? "seed-round-value-sm" : ""
                                      }`}
                                    >
                                      {card.value}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>

                          {hasExport && (
                            <div className="seed-round-card mb-3">
                              <h5 className="seed-round-section-title">
                                Last Magna CSV Export
                              </h5>
                              <div className="row">
                                <div className="col-md-6 seed-round-detail-block">
                                  <span className="seed-round-label">Bucket</span>
                                  <p>{round.lastExportBucket}</p>
                                </div>
                                <div className="col-md-6 seed-round-detail-block">
                                  <span className="seed-round-label">
                                    Exported At
                                  </span>
                                  <p>
                                    {round.lastExportAt
                                      ? moment(round.lastExportAt).format(
                                          "DD/MM/YY HH:mm",
                                        )
                                      : "—"}
                                  </p>
                                </div>
                                {/* <div className="col-12 seed-round-detail-block">
                                  <span className="seed-round-label">S3 Key</span>
                                  <p>{round.lastExportKey}</p>
                                </div> */}
                                {exportInfo?.rowCount != null && (
                                  <div className="col-md-6 seed-round-detail-block">
                                    <span className="seed-round-label">Rows</span>
                                    <p>{exportInfo.rowCount}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          <div className="seed-round-card">
                            <h5 className="seed-round-section-title">
                              Admin Actions
                            </h5>
                            <p className="seed-round-desc">
                              Export Magna CSV to private S3 at any time while the
                              round is open or closed. Each export includes only
                              new seed transactions since the last export. Magna
                              upload is handled manually by the ONMX team.
                            </p>
                            <div className="seed-round-actions">
                              <button
                                type="button"
                                className="btn seed-round-btn-open"
                                disabled={
                                  Boolean(actionLoading) ||
                                  !isClosed ||
                                  !config.seedRoundApiUrl
                                }
                                onClick={() =>
                                  runAction(
                                    "open",
                                    openSeedRoundAction,
                                    "Open Seed Round?",
                                    "Mark this seed round as OPEN again?",
                                  )
                                }
                              >
                                {actionLoading === "open" ? (
                                  <>
                                    <i className="fa fa-spinner fa-spin" />{" "}
                                    Opening...
                                  </>
                                ) : (
                                  "Open Round"
                                )}
                              </button>

                              <button
                                type="button"
                                className="btn seed-round-btn-close"
                                disabled={
                                  Boolean(actionLoading) ||
                                  isClosed ||
                                  !config.seedRoundApiUrl
                                }
                                onClick={() =>
                                  runAction(
                                    "close",
                                    closeSeedRoundAction,
                                    "Close Seed Round?",
                                    "Mark this seed round as CLOSED?",
                                  )
                                }
                              >
                                {actionLoading === "close" ? (
                                  <>
                                    <i className="fa fa-spinner fa-spin" />{" "}
                                    Closing...
                                  </>
                                ) : (
                                  "Close Round"
                                )}
                              </button>

                              <button
                                type="button"
                                className="btn seed-round-btn-export"
                                disabled={
                                  Boolean(actionLoading) ||
                                  !config.seedRoundApiUrl
                                }
                                onClick={() =>
                                  runAction(
                                    "export",
                                    exportSeedRoundAction,
                                    "Export Magna CSV?",
                                    "Export new seed transactions (since last export) to private S3?",
                                  )
                                }
                              >
                                {actionLoading === "export" ? (
                                  <>
                                    <i className="fa fa-spinner fa-spin" />{" "}
                                    Exporting...
                                  </>
                                ) : (
                                  "Export Magna CSV"
                                )}
                              </button>

                              <button
                                type="button"
                                className="btn seed-round-btn-reconcile"
                                disabled={
                                  Boolean(actionLoading) ||
                                  !config.seedRoundApiUrl
                                }
                                onClick={() =>
                                  runAction(
                                    "reconcile",
                                    reconcileSeedRoundAction,
                                    "Close + Export?",
                                    "Close round and export Magna CSV in one step?",
                                  )
                                }
                              >
                                {actionLoading === "reconcile" ? (
                                  <>
                                    <i className="fa fa-spinner fa-spin" />{" "}
                                    Processing...
                                  </>
                                ) : (
                                  "Close + Export (Reconcile)"
                                )}
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default SeedRound;
